<template>
  <div class="tor-my-school-page">
    <v-alert v-if="error" type="error" variant="tonal" class="mb-4" closable>
      {{ error }}
    </v-alert>

    <v-card variant="outlined" :loading="loading">
      <v-card-title>ТОР Моя Школа</v-card-title>
      <v-card-subtitle>
        Выберите необходимые значения. Пустые фильтры выгружают всех действующих студентов.
      </v-card-subtitle>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="filters.courses"
              autocomplete="one-time-code"
              name="tor-my-school-courses-filter"
              spellcheck="false"
              autocorrect="off"
              autocapitalize="none"
              :items="options.courses"
              item-title="title"
              item-value="value"
              label="Номер курса"
              variant="outlined"
              multiple
              chips
              closable-chips
              clearable
              :disabled="loading || downloading"
            />
          </v-col>

          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="filters.students"
              autocomplete="one-time-code"
              name="tor-my-school-students-filter"
              spellcheck="false"
              autocorrect="off"
              autocapitalize="none"
              :items="options.students"
              item-title="title"
              item-value="value"
              label="ФИО студентов"
              variant="outlined"
              multiple
              chips
              closable-chips
              clearable
              :disabled="loading || downloading"
            />
          </v-col>

          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="filters.groups"
              autocomplete="one-time-code"
              name="tor-my-school-groups-filter"
              spellcheck="false"
              autocorrect="off"
              autocapitalize="none"
              :items="options.groups"
              item-title="title"
              item-value="value"
              label="Номера групп"
              variant="outlined"
              multiple
              chips
              closable-chips
              clearable
              :disabled="loading || downloading"
            />
          </v-col>
        </v-row>

        <v-row dense class="export-options">
          <v-col cols="12" md="4">
            <v-checkbox
              v-model="optionsFlags.splitGroups"
              label="Каждая группа отдельным файлом"
              hide-details
              :disabled="downloading"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-checkbox
              v-model="optionsFlags.separateForeigners"
              label="Иностранные граждане отдельным файлом"
              hide-details
              :disabled="downloading"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-checkbox
              v-model="optionsFlags.includeStaff"
              label="Сотрудники"
              hide-details
              :disabled="downloading"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-checkbox
              v-model="includeAcademicLeave"
              label="Включить студентов в академическом отпуске в состав группы"
              hide-details
              :disabled="loading || downloading"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-checkbox
              v-model="overrideEnrollmentOrderDate"
              label="Указать дату приказа о зачислении"
              hide-details
              :disabled="downloading"
            />
          </v-col>
          <v-col v-if="overrideEnrollmentOrderDate" cols="12" md="4">
            <v-text-field
              v-model="enrollmentOrderDate"
              class="text-field-date-icon"
              autocomplete="off"
              name="tor-my-school-enrollment-order-date"
              label="Дата приказа о зачислении"
              type="date"
              variant="outlined"
              clearable
              hide-details
              :disabled="downloading"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-btn
          variant="text"
          :disabled="!hasFilters || downloading"
          @click="resetFilters"
        >
          Сбросить фильтры
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          prepend-icon="mdi-download"
          :loading="downloading"
          :disabled="loading || missingEnrollmentOrderDate"
          @click="download"
        >
          Скачать {{ archiveRequested ? 'ZIP' : 'XLSX' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
const ACADEMIC_LEAVE_GROUP_VALUE = 'academic_leave';

const emptyFilters = () => ({
  courses: [],
  students: [],
  groups: [],
});

export default {
  name: 'TorMySchoolPage',
  data() {
    return {
      loading: false,
      downloading: false,
      error: '',
      overrideEnrollmentOrderDate: false,
      enrollmentOrderDate: '',
      includeAcademicLeave: false,
      filters: emptyFilters(),
      optionsFlags: {
        splitGroups: false,
        separateForeigners: false,
        includeStaff: false,
      },
      options: {
        courses: [],
        students: [],
        groups: [],
      },
    };
  },
  computed: {
    endpoint() {
      return `${this.$model.wwwroot}/procollege/manage_groups/export/torMySchool`;
    },
    hasFilters() {
      return Object.values(this.filters).some((values) => values.length > 0);
    },
    archiveRequested() {
      return Object.values(this.optionsFlags).some(Boolean) || this.academicLeaveSelected;
    },
    academicLeaveSelected() {
      return this.filters.groups.includes(ACADEMIC_LEAVE_GROUP_VALUE);
    },
    missingEnrollmentOrderDate() {
      return this.overrideEnrollmentOrderDate && !this.enrollmentOrderDate;
    },
  },
  created() {
    this.loadOptions();
  },
  watch: {
    includeAcademicLeave() {
      this.loadOptions();
    },
    academicLeaveSelected() {
      this.loadOptions();
    },
  },
  methods: {
    async loadOptions() {
      this.loading = true;
      this.error = '';
      try {
        const response = await this.axios.get(`${this.endpoint}/getFilterOptions`, {
          params: {
            includeAcademicLeave: this.includeAcademicLeave || this.academicLeaveSelected ? 1 : 0,
          },
        });
        const data = response.data || {};
        this.options.courses = Array.isArray(data.courses) ? data.courses : [];
        this.options.students = Array.isArray(data.students) ? data.students : [];
        this.options.groups = Array.isArray(data.groups) ? data.groups : [];
      } catch (error) {
        this.error = 'Не удалось загрузить значения фильтров.';
        if (this.axiosDefaultErrorHandler) this.axiosDefaultErrorHandler(error);
      } finally {
        this.loading = false;
      }
    },
    resetFilters() {
      this.filters = emptyFilters();
    },
    async download() {
      this.downloading = true;
      this.error = '';
      try {
        const response = await this.axios.get(`${this.endpoint}/download`, {
          params: {
            ...this.filters,
            splitGroups: this.optionsFlags.splitGroups ? 1 : 0,
            separateForeigners: this.optionsFlags.separateForeigners ? 1 : 0,
            includeStaff: this.optionsFlags.includeStaff ? 1 : 0,
            includeAcademicLeave: this.includeAcademicLeave ? 1 : 0,
            overrideEnrollmentOrderDate: this.overrideEnrollmentOrderDate ? 1 : 0,
            enrollmentOrderDate: this.overrideEnrollmentOrderDate ? this.enrollmentOrderDate : '',
          },
          responseType: 'blob',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });
        const url = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.archiveRequested ? 'tor_my_school.zip' : 'tor_my_school.xlsx';
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.error = 'Не удалось сформировать файл выгрузки.';
        if (this.axiosDefaultErrorHandler) this.axiosDefaultErrorHandler(error);
      } finally {
        this.downloading = false;
      }
    },
  },
};
</script>

<style scoped>
.tor-my-school-page {
  width: 100%;
}

.export-options :deep(.v-label) {
  align-items: center;
  display: inline-flex;
  margin: 0;
}
</style>
